import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";

// Reusable 48px round trigger with a small caret badge and a dropdown menu.
// Used by NavbarButtons below md to consolidate EPK actions and preferences.
// Closes on outside tap and Esc. Focus returns to trigger on close.
export default function NavbarDropdown({ icon, ariaLabel, children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const { theme } = useTheme();
  const isLight = theme === "light";

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  // Close on Esc, return focus
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div className="tw-relative tw-inline-block">
      <button
        ref={triggerRef}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        className="tw-relative tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#FF43A7]/40 tw-bg-transparent tw-text-[#FF43A7] tw-transition-all hover:tw-bg-[#FF43A7]/15 tw-cursor-pointer tw-shrink-0"
      >
        <FontAwesomeIcon icon={icon} className="tw-text-base" />
        <span className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#FF43A7]/20 tw-text-[#FF43A7]">
          <FontAwesomeIcon icon={faCaretDown} className="tw-text-[10px]" />
        </span>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={`tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-min-w-[180px] tw-rounded-xl tw-border tw-border-[#FF43A7]/30 tw-shadow-2xl tw-z-[9999] tw-overflow-hidden ${
            isLight ? "tw-bg-white tw-shadow-black/10" : "tw-bg-[#1C0039] tw-shadow-black/60"
          }`}
        >
          {React.Children.map(children, (child) => {
            if (!child) return null;
            // Clone children and pass onClose so items can close the dropdown
            return React.cloneElement(child, {
              onDropdownClose: () => {
                setOpen(false);
                if (triggerRef.current) triggerRef.current.focus();
              },
            });
          })}
        </div>
      )}
    </div>
  );
}

// A single menu item inside NavbarDropdown.
// 44px min-height as per spec.
export function NavbarDropdownItem({ icon, label, onClick, href, onDropdownClose }) {
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (onDropdownClose) onDropdownClose();
  };

  const classes = "tw-flex tw-items-center tw-gap-3 tw-px-4 tw-min-h-[44px] tw-w-full tw-text-sm tw-font-medium tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/10 tw-transition-colors tw-cursor-pointer tw-no-underline tw-border-0 tw-bg-transparent";

  if (href) {
    return (
      <Link to={href} role="menuitem" onClick={handleClick} className={classes}>
        {icon && <FontAwesomeIcon icon={icon} className="tw-text-sm tw-w-4" />}
        <span>{label}</span>
      </Link>
    );
  }

  return (
    <button type="button" role="menuitem" onClick={handleClick} className={classes}>
      {icon && <FontAwesomeIcon icon={icon} className="tw-text-sm tw-w-4" />}
      <span>{label}</span>
    </button>
  );
}
